import { isDefined } from "@clipboard-health/util-ts";

import { cognitoConfig } from "./config";

/**
 * Runs Cognito authentication without touching Amplify's in-memory or persisted
 * auth state. EMAIL_OTP challenges and password probes use the same raw Cognito
 * JSON protocol here so callers never duplicate request/error handling.
 *
 * Side-channel tokens are returned only to the immediate caller. This module
 * never writes them to Amplify or browser storage.
 */

const EMAIL_OTP_CHALLENGE = "EMAIL_OTP";
const NEW_PASSWORD_REQUIRED_CHALLENGE = "NEW_PASSWORD_REQUIRED";

// A stalled Cognito call must not wedge an auth flow, so every request carries
// a hard timeout.
const COGNITO_REQUEST_TIMEOUT_MS = 30_000;

interface CognitoErrorBody {
  __type?: string;
  message?: string;
}

interface CognitoRequestParams {
  target: string;
  body: Record<string, unknown>;
  keepalive?: boolean;
}

interface AuthenticationResult {
  IdToken?: string;
  RefreshToken?: string;
}

interface ChallengeResponse {
  ChallengeName?: string;
  Session?: string;
  AuthenticationResult?: AuthenticationResult;
}

export class CognitoSideChannelError extends Error {
  constructor(
    public readonly type: string,
    message: string,
    public readonly challengeName?: string
  ) {
    super(message);
    this.name = "CognitoSideChannelError";
  }

  public get isWrongOtp(): boolean {
    return this.type === "CodeMismatchException" || this.type === "ExpiredCodeException";
  }

  public get isExpiredSession(): boolean {
    return this.type === "NotAuthorizedException" || this.type === "ExpiredSessionException";
  }

  /** Cognito throttles per-user auth attempts; retrying immediately only extends the lockout. */
  public get isRateLimited(): boolean {
    return this.type === "LimitExceededException" || this.type === "TooManyRequestsException";
  }

  public get isNewPasswordRequired(): boolean {
    return (
      this.type === "ChallengeRequired" && this.challengeName === NEW_PASSWORD_REQUIRED_CHALLENGE
    );
  }
}

/**
 * Starts the EMAIL_OTP challenge; the code is delivered to the user's email. The
 * username must be replayed verbatim in `verifyEmailOtpSideChannel`. An account that
 * can't take this challenge answers with a different challenge, which is surfaced as
 * `UnexpectedChallenge` so the caller can steer to password.
 */
export async function requestEmailOtpSideChannel(username: string): Promise<{ session: string }> {
  const response = await cognitoRequest<ChallengeResponse>({
    target: "InitiateAuth",
    body: {
      AuthFlow: "USER_AUTH",
      ClientId: cognitoConfig.clientId,
      AuthParameters: {
        USERNAME: username,
        PREFERRED_CHALLENGE: EMAIL_OTP_CHALLENGE,
      },
    },
  });

  if (response.ChallengeName !== EMAIL_OTP_CHALLENGE || !response.Session) {
    throw new CognitoSideChannelError(
      "UnexpectedChallenge",
      `Expected ${EMAIL_OTP_CHALLENGE} challenge, got ${response.ChallengeName ?? "none"}`
    );
  }

  return { session: response.Session };
}

/** Confirms the emailed OTP code and returns the freshly minted side-channel tokens. */
export async function verifyEmailOtpSideChannel(params: {
  /** Must replay the exact USERNAME the InitiateAuth call used. */
  username: string;
  session: string;
  code: string;
}): Promise<{ freshIdToken: string; refreshToken?: string }> {
  const response = await cognitoRequest<ChallengeResponse>({
    target: "RespondToAuthChallenge",
    body: {
      ChallengeName: EMAIL_OTP_CHALLENGE,
      ClientId: cognitoConfig.clientId,
      Session: params.session,
      ChallengeResponses: {
        USERNAME: params.username,
        EMAIL_OTP_CODE: params.code,
      },
    },
  });

  const freshIdToken = response.AuthenticationResult?.IdToken;
  if (!freshIdToken) {
    throw new CognitoSideChannelError(
      "NoAuthenticationResult",
      "OTP challenge did not return tokens"
    );
  }

  const refreshToken = response.AuthenticationResult?.RefreshToken;
  return isDefined(refreshToken) ? { freshIdToken, refreshToken } : { freshIdToken };
}

/**
 * Probes email + password over raw `InitiateAuth USER_PASSWORD_AUTH`. Returned
 * tokens never enter Amplify storage. A NEW_PASSWORD_REQUIRED response is
 * identified separately so login can deliberately hand the challenge to Amplify.
 */
export async function signInWithPasswordSideChannel(params: {
  email: string;
  password: string;
}): Promise<{ freshIdToken: string; refreshToken?: string }> {
  const response = await cognitoRequest<ChallengeResponse>({
    target: "InitiateAuth",
    body: {
      AuthFlow: "USER_PASSWORD_AUTH",
      ClientId: cognitoConfig.clientId,
      AuthParameters: { USERNAME: params.email, PASSWORD: params.password },
    },
  });

  const freshIdToken = response.AuthenticationResult?.IdToken;
  if (!freshIdToken) {
    throw new CognitoSideChannelError(
      "ChallengeRequired",
      `Password sign-in needs ${response.ChallengeName ?? "an additional challenge"} — use the emailed code instead`,
      response.ChallengeName
    );
  }

  const refreshToken = response.AuthenticationResult?.RefreshToken;
  return isDefined(refreshToken) ? { freshIdToken, refreshToken } : { freshIdToken };
}

/** Revokes a discarded side-channel refresh token without an authenticated session. */
export async function revokeTokenSideChannel(params: { refreshToken: string }): Promise<void> {
  await cognitoRequest<Record<string, never>>({
    target: "RevokeToken",
    body: {
      ClientId: cognitoConfig.clientId,
      Token: params.refreshToken,
    },
    // Login does not wait for this best-effort cleanup. Keep the request alive if
    // the successful Amplify sign-in navigates away before Cognito responds.
    keepalive: true,
  });
}

async function cognitoRequest<T>({
  target,
  body,
  keepalive = false,
}: CognitoRequestParams): Promise<T> {
  try {
    const [region] = cognitoConfig.userPoolId.split("_");
    const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(COGNITO_REQUEST_TIMEOUT_MS),
      keepalive,
    });

    const responseBody = (await response.json()) as T & CognitoErrorBody;
    if (!response.ok) {
      const type = (responseBody.__type ?? "UnknownCognitoError").split("#").pop() ?? "Unknown";
      throw new CognitoSideChannelError(type, responseBody.message ?? "Cognito request failed");
    }

    return responseBody;
  } catch (error: unknown) {
    if (error instanceof CognitoSideChannelError) {
      throw error;
    }

    throw new CognitoSideChannelError("NetworkError", "Network error occurred");
  }
}
